import api, { durableBusinessPost } from './api';
import { messageErreur } from './erreurs';
import { libelle, referenceLisible } from './libelles';
import { tripOption } from './selectOptions';

/**
 * Appels du lot 017: frais, charges fixes récurrentes, observations.
 * Contrat: spec 017 (frais et observations).
 *
 * Les réponses de succès arrivent en `{ data: … }`; ces fonctions rendent
 * directement le contenu utile.
 */

/** Catégories qu'on peut saisir à la main (l'assurance et le loyer viennent d'ailleurs). */
export const CATEGORIES_SAISIE = [
  'FRAIS_ROUTE',
  'FRAIS_SUPP',
  'SALAIRE',
  'ENTRETIEN_REPARATION',
  'PIECES_DETACHEES',
  'PIECES_ADMIN',
  'AUTRES',
];

export const PORTEURS_FRAIS = ['SOCIETE', 'PROPRIETAIRE', 'CLIENT'];

const contenu = (response) => response?.data?.data ?? response?.data ?? null;

const nettoyer = (params) =>
  Object.fromEntries(
    Object.entries(params || {}).filter(([, valeur]) => valeur !== undefined && valeur !== null && valeur !== ''),
  );

/** Clé d'idempotence d'une saisie: la même clé rejouée ne crée jamais de doublon. */
export const cleFrais = (prefixe = 'frais') => {
  const hasard =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  return `${prefixe}-${hasard}`;
};

// --- Frais (journal) ---------------------------------------------------------------------

/**
 * Frais du journal, avec totaux sur tout le filtre.
 * Rend `{ items, total, totals, page, pageSize }`.
 */
export async function listerFrais(params = {}) {
  const response = await api.get('/finance/expenses', { params: nettoyer(params) });
  const corps = response?.data || {};
  return {
    items: Array.isArray(corps.data) ? corps.data : [],
    total: corps.total ?? 0,
    page: corps.page ?? 1,
    pageSize: corps.page_size ?? 50,
    totals: corps.totals || { amount: 0, count: 0, by_category: [] },
  };
}

/** Enregistre un frais (corps du §2 du contrat). */
export async function enregistrerFrais(corps) {
  const response = await durableBusinessPost('finance_operation', 'create', '/finance/operations', corps);
  return { operation: response?.data?.data ?? null };
}

// --- Charges fixes (direction seulement) -----------------------------------------------

export async function chargesFixesDuCamion(truckId) {
  return contenu(await api.get(`/trucks/${truckId}/charge-rules`)) || [];
}

export async function chargesPrevues({ month, truckIds } = {}) {
  const params = new URLSearchParams();
  if (month) params.set('month', month);
  (truckIds || []).forEach((id) => params.append('truck_id', id));
  return contenu(await api.get('/charge-rules/expected', { params })) || [];
}

export async function creerChargeFixe(truckId, corps) {
  return contenu(await api.post(`/trucks/${truckId}/charge-rules`, corps));
}

export async function modifierChargeFixe(ruleId, corps) {
  return contenu(await api.patch(`/charge-rules/${ruleId}`, corps));
}

export async function supprimerChargeFixe(ruleId) {
  return contenu(await api.delete(`/charge-rules/${ruleId}`));
}

/** Paie le mois d'une charge fixe: `{ operation, already_paid, month }`. */
export async function payerChargeFixe(ruleId, corps) {
  return contenu(await api.post(`/charge-rules/${ruleId}/pay`, corps));
}

// --- Observations -----------------------------------------------------------------------

/** Flux d'un camion, d'un trajet ou d'un convoi: `{ items, total }`. */
export async function listerObservations(entityType, entityId, { limit = 100, offset = 0 } = {}) {
  const response = await api.get('/observations', {
    params: { entity_type: entityType, entity_id: entityId, limit, offset },
  });
  const corps = response?.data || {};
  return { items: Array.isArray(corps.data) ? corps.data : [], total: corps.total ?? 0 };
}

export async function ajouterObservation(corps, cle) {
  const response = await api.post('/observations', corps, cle ? { headers: { 'Idempotency-Key': cle } } : undefined);
  return contenu(response);
}

export async function supprimerObservation(id) {
  return contenu(await api.delete(`/observations/${id}`));
}

// --- Messages d'erreur français ---------------------------------------------------------

/**
 * Message à afficher pour une erreur de ces appels: le serveur écrit déjà en
 * français ses règles métier (`lib/erreurs.js` les garde); une erreur de forme
 * ou un texte en anglais est remplacé par une phrase de l'écran.
 */
export function messageErreurFrais(error, repli = 'Vérifiez les champs saisis.') {
  return messageErreur(error, repli);
}

// --- Options de listes (SearchableSelect) -----------------------------------------------

/** « AB-123-CD · Bamako → Dakar · 13/10/2026 » : voir `tripOption`. */
export const optionTrajet = (trip, contexte) => tripOption(trip, contexte);

/** Un convoi se reconnaît à son numéro et à son parcours. */
export const optionConvoi = (operation) => ({
  value: operation.id,
  label: referenceLisible(operation.code, 'Convoi sans numéro'),
  description: operation.route || undefined,
  keywords: [operation.route, operation.convoy_chief_name],
});

export const optionCompte = (account) => ({
  value: account.id,
  label: account.name || 'Compte sans nom',
  description: account.account_type ? libelle('compteTresorerie', account.account_type) : undefined,
});

export const optionClient = (client) => ({
  value: client.id,
  label: client.name || client.company_name || 'Client sans nom',
  description: client.city || client.phone || undefined,
  keywords: [client.contact_name, client.email],
});
